import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useContext, useMemo } from 'react';

import Link from 'components/shared/link';
import MainContext from 'context/main';
import MastodonLogo from 'icons/mastodon.inline.svg';
import XLogo from 'icons/x-com.inline.svg';
import Logo from 'images/logo.inline.svg';
import getFormattedId from 'utils/get-formatted-id';

import LangSelect from '../lang-select';

import GithubLogo from './images/footer-github-logo.inline.svg';

const getHomeLink = (lang) => {
  if (lang === 'ca') return '/cat';
  if (lang === 'ja') return '/jp';

  return '/';
};

const Footer = ({ theme }) => {
  const {
    menus: { footerMenuItems: items, footerJPMenuItems: itemsJP, footerKOMenuItems: itemsKO },
    globalFields: { copyright, githubUrl, twitterUrl, mastodonUrl },
    translations,
    language,
  } = useContext(MainContext);

  const menuItems =
    {
      ja: itemsJP,
      ko: itemsKO,
      en: items,
    }[language?.locale] || items;

  const menuIds = useMemo(
    () =>
      menuItems.map(({ childItems }) =>
        childItems.nodes.map(({ label }) => `f-${getFormattedId(label)}`)
      ),
    [menuItems]
  );

  const homeUrl = getHomeLink(language?.locale);

  return (
    <footer
      className={clsx('safe-paddings', {
        'bg-new-gray-2': theme === 'gray',
      })}
    >
      <div className="container grid-gap-x grid grid-cols-12 pb-18 pt-20 xl:pb-9 xl:pt-11 lg:pb-14 lg:pt-16 md:flex md:flex-col md:items-start md:py-10 xs:py-8">
        <Link className="hidden md:inline-block" to={homeUrl} title="← Back to the homepage">
          <Logo className="h-10" aria-hidden />
          <span className="sr-only">RevenueCat</span>
        </Link>
        <div className="col-span-4 flex flex-col justify-end xl:col-span-3 lg:col-span-5 md:order-last md:mt-9 xs:mt-7 xs:w-full">
          <Link
            className="mb-auto inline-block md:hidden"
            to={homeUrl}
            title="← Back to the homepage"
          >
            <Logo className="h-10" aria-hidden />
            <span className="sr-only">RevenueCat</span>
          </Link>
          {translations && translations.length > 0 && (
            <LangSelect className="mb-7 hidden xl:block xs:mb-5 xs:w-full" openTo="top" />
          )}
          <nav>
            <ul className="flex space-x-3">
              <li>
                <Link
                  className="inline-block align-top text-new-black hover:text-[#404062]"
                  to={githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <GithubLogo className="h-9 transition-colors duration-200" aria-hidden />
                  <span className="sr-only">Github</span>
                </Link>
              </li>
              <li>
                <Link
                  className="inline-block align-top text-new-black hover:text-[#404062]"
                  to={twitterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <XLogo className="h-9 transition-colors duration-200" aria-hidden />
                  <span className="sr-only">Twitter</span>
                </Link>
              </li>
              <li>
                <Link
                  className="inline-block align-top text-new-black hover:text-[#404062]"
                  to={mastodonUrl}
                  target="_blank"
                  rel="me"
                >
                  <MastodonLogo className="h-9 transition-colors duration-200" aria-hidden />
                  <span className="sr-only">Mastodon</span>
                </Link>
              </li>
            </ul>
          </nav>
          <p className="mt-7 text-sm leading-none text-new-black xs:mt-5">
            Ⓒ {new Date().getFullYear()} {copyright}
          </p>
        </div>
        <nav className="col-span-8 flex space-x-16 pt-4 xl:col-span-9 xl:space-x-10 lg:col-span-7 lg:grid lg:grid-cols-2 lg:gap-8 lg:space-x-0 md:mt-5 md:w-full md:gap-x-4 md:gap-y-9 md:pt-0 xs:mt-4 xs:block xs:space-y-7">
          {menuItems.map(({ label, childItems }, index) => {
            const itemIds = menuIds[index];

            return (
              <div key={index}>
                <h3 className="text-base font-medium leading-none text-new-black">{label}</h3>
                <ul className="mt-5 space-y-2 lg:mt-3 lg:space-y-1">
                  {childItems.nodes.map(({ label, path, target }, index) => (
                    <li key={index}>
                      <Link
                        className="text-nowrap py-2 text-sm !leading-tight"
                        to={path}
                        target={target}
                        id={itemIds[index]}
                        theme="black"
                        includeBaseStyles
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  theme: PropTypes.oneOf(['gray', 'white']),
};

Footer.defaultProps = {
  theme: 'white',
};

export default Footer;
